import React, { useEffect, useMemo } from 'react';
import { Row, Col, Card, Spinner, Container } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { fetchMenuChart, selectDays, selectWeekName, selectLoading, selectError } from '../../../store/menuChart/menuChartSlice';

const MenuChart = () => {
    const dispatch = useDispatch();
    const days = useSelector(selectDays);
    const weekName = useSelector(selectWeekName);
    const loading = useSelector(selectLoading);
    const error = useSelector(selectError);

    useEffect(() => {
        dispatch(fetchMenuChart());
    }, [dispatch]);

    const normalizeMenuType = (type = '') => {
        const upper = type.toString().trim().toUpperCase();
        if (upper === 'MORNING SNAK' || upper === 'MORNING SNAC') return 'MORNING SNACK';
        return upper;
    };

    // Preferred order, but include any additional API menu types too.
    const preferredMenuTypes = ['MORNING SNACK', 'LUNCH', 'AFTERNOON SNACK'];

    const menuTypes = useMemo(() => {
        const typeSet = new Set();

        days.forEach((day) => {
            (Array.isArray(day?.menuTypes) ? day.menuTypes : []).forEach((mt) => {
                const normalized = normalizeMenuType(mt?.menu_type);
                if (normalized) {
                    typeSet.add(normalized);
                }
            });
        });

        const orderedPreferred = preferredMenuTypes.filter((type) => typeSet.has(type));
        const additionalTypes = Array.from(typeSet).filter((type) => !preferredMenuTypes.includes(type));
        return [...orderedPreferred, ...additionalTypes];
    }, [days]);

    return (
        <>
            <Container>
                <Row>
                    <Col sm="12">
                        <Card>
                            <Card.Header className="d-flex justify-content-between">
                                <div className="header-title">
                                    <h4 className="card-title">This Week's Menu </h4>
                                </div>
                            </Card.Header>
                            <Card.Body>
                                {loading ? (
                                    <div className="text-center p-5">
                                        <Spinner animation="border" role="status">
                                            <span className="visually-hidden">Loading...</span>
                                        </Spinner>
                                    </div>
                                ) : error ? (
                                    <div className="alert alert-danger" role="alert">
                                        {error}
                                    </div>
                                ) : days.length === 0 ? (
                                    <div className="text-center p-5">
                                        <p className="text-muted">No menu data available</p>
                                    </div>
                                ) : (
                                    <div style={{ overflowX: 'auto' }}>
                                        {weekName && (
                                            <div className="mb-3 fw-semibold text-primary">
                                                Week: {weekName}
                                            </div>
                                        )}
                                        <table className="table table-bordered align-middle text-center">
                                            <thead>
                                                <tr>
                                                    <th className="bg-primary text-white" style={{ minWidth: 180 }}>Day</th>
                                                    {menuTypes.map((menuType, index) => (
                                                        <th key={index} className="bg-primary text-white" style={{ minWidth: 180 }}>{menuType}</th>
                                                    ))}
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {days.map((day, dayIndex) => (
                                                    <tr key={dayIndex} className={dayIndex % 2 === 0 ? '' : 'bg-light'}>
                                                        <td className="fw-bold text-primary" style={{ minWidth: 180 }}>{day.dayName}</td>
                                                        {menuTypes.map((menuType, typeIndex) => {
                                                            const menuTypeObj = Array.isArray(day.menuTypes)
                                                                ? day.menuTypes.find(mt => normalizeMenuType(mt?.menu_type) === menuType)
                                                                : null;
                                                            const items = menuTypeObj?.menu_items || [];
                                                            return (
                                                                <td key={typeIndex} style={{ minWidth: 180 }}>
                                                                    {items.length > 0 ? (
                                                                        <ul className="mb-0 ps-3 text-start" style={{ listStyle: 'disc inside' }}>
                                                                            {items.map((item, itemIndex) => (
                                                                                <li key={itemIndex} style={{ color: '#212529', marginBottom: '2px' }}>{item}</li>
                                                                            ))}
                                                                        </ul>
                                                                    ) : (
                                                                        <span className="text-secondary">-</span>
                                                                    )}
                                                                </td>
                                                            );
                                                        })}
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            </Container>
        </>
    );
};

export default MenuChart;
